/**
 * Editor Store — central Zustand state for the canvas editor
 *
 * Manages:
 * - current story and pages
 * - selected layers & active page
 * - editor UI state (tool, sidebar tab, preview mode)
 * - undo/redo history
 * - autosave
 */

import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { v4 as uuid } from 'uuid';
import { storiesApi } from '../api/client';

// ── Default values ───────────────────────────────────────────

export const DEFAULT_TRANSFORM = {
  x: 10, y: 10, width: 80, height: 20,
  rotation: 0, opacity: 1, zIndex: 1,
};

export const DEFAULT_TEXT_STYLE = {
  fontFamily: 'Inter',
  fontSize: 32,
  fontWeight: '700',
  lineHeight: 1.3,
  letterSpacing: 0,
  textAlign: 'center',
  textTransform: 'none',
  color: '#ffffff',
  backgroundColor: null,
  backgroundOpacity: 1,
  padding: [8, 12, 8, 12],
  borderRadius: 4,
  textShadow: null,
  stroke: null,
  strokeWidth: 0,
};

// ── Helpers ──────────────────────────────────────────────────

function normalizeStory(story) {
  return {
    ...story,
    id: story.id || story._id,
    metadata: {
      title: story.title || '',
      publisherName: story.publisherName || '',
      description: story.description || '',
      ...(story.metadata ?? story.storyMetadata ?? {}),
    },
    pages: (story.pages || []).map((p) => ({
      ...p,
      id: p.id || p._id,
      layers: (p.layers || [])
        .filter((l) => l.data != null)
        .map((l, i) => ({
          ...l,
          id: l.id || l._id || `layer-${i}`,
        })),
    })),
  };
}

// ── Store implementation ─────────────────────────────────────

export const useEditorStore = create()(
  immer((set, get) => ({
    story: null,
    isSaving: false,
    isDirty: false,
    lastSavedAt: null,
    saveError: null,

    activePageId: null,
    selectedLayerIds: [],
    hoveredLayerId: null,
    editingLayerId: null,

    activeTool: 'select',
    activeSidebarTab: 'elements',
    previewMode: 'edit',
    showValidationPanel: false,
    showAmpCodePanel: false,
    isMediaLibraryOpen: false,
    isExporting: false,
    zoomLevel: 1,

    history: [],
    historyIndex: -1,
    validationResult: null,

    // ── Story loading ──────────────────────────────────────

    loadStory: async (storyId) => {
      const raw = await storiesApi.get(storyId);
      const story = normalizeStory(raw);

      if (story.pages.length === 0) {
        const pageId = uuid();
        story.pages.push({
          id: pageId,
          storyId: story.id,
          pageIndex: 0,
          name: 'Page 1',
          background: { type: 'color', color: '#1a1a2e' },
          layers: [],
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        });
      }

      set((state) => {
        state.story = story;
        state.activePageId = story.pages[0]?.id ?? null;
        state.selectedLayerIds = [];
        state.isDirty = !(raw.pages?.length);
        state.history = [];
        state.historyIndex = -1;
      });
    },

    // Lightweight refresh used by AI generation polling — does NOT reset editor state
    pollStory: async (storyId) => {
      const raw = await storiesApi.get(storyId);
      const story = normalizeStory(raw);
      set((state) => {
        if (!state.story || state.story.id !== story.id) return;
        state.story.pages = story.pages;
        state.story.metadata = story.metadata;
        state.story.updatedAt = story.updatedAt;
        // Preserve active page if it still exists, else fall back to first
        const pageIds = new Set(story.pages.map((p) => p.id));
        if (!state.activePageId || !pageIds.has(state.activePageId)) {
          state.activePageId = story.pages[0]?.id ?? null;
        }
      });
    },

    bootstrapLocalStory: () => {
      const pageId = uuid();
      const story = {
        id: 'local',
        title: 'Untitled Story',
        metadata: { width: 1080, height: 1920 },
        pages: [
          {
            id: pageId,
            storyId: 'local',
            pageIndex: 0,
            name: 'Page 1',
            background: { type: 'color', color: '#1a1a2e' },
            layers: [],
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          },
        ],
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      set((state) => {
        state.story = story;
        state.activePageId = pageId;
        state.selectedLayerIds = [];
        state.isDirty = false;
        state.history = [];
        state.historyIndex = -1;
      });
    },

    setStory: (incoming) => set((state) => {
      const story = normalizeStory(incoming);
      state.story = story;
      const existingPageIds = new Set(story.pages.map((p) => p.id));
      if (!state.activePageId || !existingPageIds.has(state.activePageId)) {
        state.activePageId = story.pages[0]?.id ?? null;
      }
      state.selectedLayerIds = [];
      state.isDirty = true;
    }),

    // ── Page actions ───────────────────────────────────────

    setActivePage: (pageId) => set((state) => {
      state.activePageId = pageId;
      state.selectedLayerIds = [];
      state.editingLayerId = null;
    }),

    getActivePage: () => {
      const { story, activePageId } = get();
      if (!story || !activePageId) return null;
      return story.pages.find((p) => p.id === activePageId) ?? null;
    },

    addPage: (afterIndex) => {
      get().pushHistory('add page');
      set((state) => {
      if (!state.story) return;
      const pages = state.story.pages;
      const insertAt = afterIndex !== undefined ? afterIndex + 1 : pages.length;
      const now = new Date().toISOString();

      const newPage = {
        id: uuid(),
        storyId: state.story.id,
        pageIndex: insertAt,
        name: `Page ${pages.length + 1}`,
        background: { type: 'color', color: '#000000' },
        layers: [],
        createdAt: now,
        updatedAt: now,
      };

      // Shift indices
      pages.forEach((p) => {
        if (p.pageIndex >= insertAt) p.pageIndex++;
      });

      pages.splice(insertAt, 0, newPage);
      state.activePageId = newPage.id;
      state.selectedLayerIds = [];
      state.isDirty = true;
      });
    },

    deletePage: (pageId) => {
      get().pushHistory('delete page');
      set((state) => {
      if (!state.story) return;
      const idx = state.story.pages.findIndex((p) => p.id === pageId);
      if (idx === -1) return;

      state.story.pages.splice(idx, 1);

      // Re-index
      state.story.pages.forEach((p, i) => { p.pageIndex = i; });

      // Update active page if needed
      if (state.activePageId === pageId) {
        state.activePageId = state.story.pages[Math.max(0, idx - 1)]?.id ?? null;
      }
      state.isDirty = true;
      });
    },

    duplicatePage: (pageId) => {
      get().pushHistory('duplicate page');
      set((state) => {
      if (!state.story) return;
      const page = state.story.pages.find((p) => p.id === pageId);
      if (!page) return;

      const now = new Date().toISOString();
      const dupPage = {
        ...JSON.parse(JSON.stringify(page)),
        id: uuid(),
        pageIndex: page.pageIndex + 1,
        name: `${page.name || 'Page'} (Copy)`,
        createdAt: now,
        updatedAt: now,
        layers: page.layers.map((l) => ({
          ...JSON.parse(JSON.stringify(l)),
          id: uuid(),
          pageId: '',
        })),
      };

      dupPage.layers.forEach((l) => { l.pageId = dupPage.id; });

      state.story.pages.forEach((p) => {
        if (p.pageIndex > page.pageIndex) p.pageIndex++;
      });

      state.story.pages.splice(page.pageIndex + 1, 0, dupPage);
      state.activePageId = dupPage.id;
      state.isDirty = true;
      });
    },

    updatePageBackground: (pageId, background) => set((state) => {
      const page = state.story?.pages.find((p) => p.id === pageId);
      if (!page) return;
      page.background = background;
      page.updatedAt = new Date().toISOString();
      state.isDirty = true;
    }),

    updatePageName: (pageId, name) => set((state) => {
      const page = state.story?.pages.find((p) => p.id === pageId);
      if (!page) return;
      page.name = name;
      state.isDirty = true;
    }),

    updatePageTransition: (pageId, transition) => set((state) => {
      const page = state.story?.pages.find((p) => p.id === pageId);
      if (!page) return;
      page.transition = transition;
      state.isDirty = true;
    }),

    updatePageAutoAdvanceDuration: (pageId, duration) => set((state) => {
      const page = state.story?.pages.find((p) => p.id === pageId);
      if (!page) return;
      page.autoAdvanceDuration = duration;
      state.isDirty = true;
    }),

    updatePageMetadata: (pageId, metadata) => set((state) => {
      const page = state.story?.pages.find((p) => p.id === pageId);
      if (!page) return;
      page.metadata = { ...page.metadata, ...metadata };
      state.isDirty = true;
    }),

    reorderPages: (fromIndex, toIndex) => set((state) => {
      if (!state.story) return;
      const pages = state.story.pages;
      const [moved] = pages.splice(fromIndex, 1);
      pages.splice(toIndex, 0, moved);
      pages.forEach((p, i) => { p.pageIndex = i; });
      state.isDirty = true;
    }),

    updatePageThumbnail: (pageId, thumbnail) => set((state) => {
      const page = state.story?.pages.find((p) => p.id === pageId);
      if (!page) return;
      page.thumbnail = thumbnail;
    }),

    // ── Layer actions ──────────────────────────────────────

    addLayer: (pageId, layerData, transformOverrides) => {
      get().pushHistory('add layer');
      const newLayer = {
        id: uuid(),
        pageId,
        name: getDefaultLayerName(layerData.type),
        locked: false,
        hidden: false,
        transform: {
          ...DEFAULT_TRANSFORM,
          ...transformOverrides,
          zIndex: getNextZIndex(get().story?.pages.find((p) => p.id === pageId)?.layers ?? []),
        },
        data: layerData,
      };

      set((state) => {
        const page = state.story?.pages.find((p) => p.id === pageId);
        if (!page) return;
        page.layers.push(newLayer);
        state.selectedLayerIds = [newLayer.id];
        state.isDirty = true;
      });

      return newLayer;
    },

    updateLayer: (layerId, updates) => set((state) => {
      const page = findPageForLayer(state.story, layerId);
      if (!page) return;
      const layerIdx = page.layers.findIndex((l) => l.id === layerId);
      if (layerIdx === -1) return;
      Object.assign(page.layers[layerIdx], updates);
      page.updatedAt = new Date().toISOString();
      state.isDirty = true;
    }),

    updateLayerTransform: (layerId, transform) => set((state) => {
      const page = findPageForLayer(state.story, layerId);
      if (!page) return;
      const layer = page.layers.find((l) => l.id === layerId);
      if (!layer) return;
      Object.assign(layer.transform, transform);
      state.isDirty = true;
    }),

    updateLayerData: (layerId, data) => {
      get().pushHistory('update layer');
      set((state) => {
        const page = findPageForLayer(state.story, layerId);
        if (!page) return;
        const layer = page.layers.find((l) => l.id === layerId);
        if (!layer) return;
        Object.assign(layer.data, data);
        state.isDirty = true;
      });
    },

    updateLayerAnimation: (layerId, animation) => {
      get().pushHistory('update animation');
      set((state) => {
        const page = findPageForLayer(state.story, layerId);
        if (!page) return;
        const layer = page.layers.find((l) => l.id === layerId);
        if (!layer) return;
        layer.animation = animation;
        state.isDirty = true;
      });
    },

    deleteLayer: (layerId) => {
      get().pushHistory('delete layer');
      set((state) => {
        const page = findPageForLayer(state.story, layerId);
        if (!page) return;
        page.layers = page.layers.filter((l) => l.id !== layerId);
        state.selectedLayerIds = state.selectedLayerIds.filter((id) => id !== layerId);
        state.isDirty = true;
      });
    },

    duplicateLayer: (layerId) => {
      get().pushHistory('duplicate layer');
      set((state) => {
        const page = findPageForLayer(state.story, layerId);
        if (!page) return;
        const layer = page.layers.find((l) => l.id === layerId);
        if (!layer) return;
        const dup = {
          ...JSON.parse(JSON.stringify(layer)),
          id: uuid(),
          name: `${layer.name} (Copy)`,
          transform: {
            ...layer.transform,
            x: layer.transform.x + 2,
            y: layer.transform.y + 2,
            zIndex: layer.transform.zIndex + 1,
          },
        };
        page.layers.push(dup);
        state.selectedLayerIds = [dup.id];
        state.isDirty = true;
      });
    },

    reorderLayer: (layerId, direction) => set((state) => {
      const page = findPageForLayer(state.story, layerId);
      if (!page) return;
      const layer = page.layers.find((l) => l.id === layerId);
      if (!layer) return;

      // Sort by current zIndex so we can work with positional indices
      const sorted = [...page.layers].sort((a, b) => a.transform.zIndex - b.transform.zIndex);
      const idx = sorted.findIndex((l) => l.id === layerId);

      if (direction === 'forward' && idx < sorted.length - 1) {
        const tmp = sorted[idx + 1];
        sorted[idx + 1] = sorted[idx];
        sorted[idx] = tmp;
      } else if (direction === 'backward' && idx > 0) {
        const tmp = sorted[idx - 1];
        sorted[idx - 1] = sorted[idx];
        sorted[idx] = tmp;
      } else if (direction === 'front' && idx < sorted.length - 1) {
        sorted.splice(idx, 1);
        sorted.push(layer);
      } else if (direction === 'back' && idx > 0) {
        sorted.splice(idx, 1);
        sorted.unshift(layer);
      }

      // Always reassign sequential 1-based values so zIndex never goes 0 or negative
      sorted.forEach((l, i) => { l.transform.zIndex = i + 1; });
      state.isDirty = true;
    }),

    toggleLayerLock: (layerId) => set((state) => {
      const page = findPageForLayer(state.story, layerId);
      if (!page) return;
      const layer = page.layers.find((l) => l.id === layerId);
      if (!layer) return;
      layer.locked = !layer.locked;
      state.isDirty = true;
    }),

    toggleLayerVisibility: (layerId) => set((state) => {
      const page = findPageForLayer(state.story, layerId);
      if (!page) return;
      const layer = page.layers.find((l) => l.id === layerId);
      if (!layer) return;
      layer.hidden = !layer.hidden;
      state.isDirty = true;
    }),

    groupSelect: (layerIds) => set((state) => {
      state.selectedLayerIds = layerIds;
    }),

    // ── Selection ──────────────────────────────────────────

    selectLayer: (layerId, multiSelect = false) => set((state) => {
      if (multiSelect) {
        if (state.selectedLayerIds.includes(layerId)) {
          state.selectedLayerIds = state.selectedLayerIds.filter((id) => id !== layerId);
        } else {
          state.selectedLayerIds.push(layerId);
        }
      } else {
        state.selectedLayerIds = [layerId];
      }
    }),

    deselectAll: () => set((state) => {
      state.selectedLayerIds = [];
      state.editingLayerId = null;
    }),

    setHoveredLayer: (layerId) => set((state) => {
      state.hoveredLayerId = layerId;
    }),

    setEditingLayer: (layerId) => set((state) => {
      state.editingLayerId = layerId;
    }),

    // ── UI ─────────────────────────────────────────────────

    setActiveTool: (tool) => set((state) => { state.activeTool = tool; }),
    setActiveSidebarTab: (tab) => set((state) => { state.activeSidebarTab = tab; }),
    setPreviewMode: (mode) => set((state) => { state.previewMode = mode; }),
    toggleValidationPanel: () => set((state) => { state.showValidationPanel = !state.showValidationPanel; }),
    toggleAmpCodePanel: () => set((state) => { state.showAmpCodePanel = !state.showAmpCodePanel; }),
    toggleMediaLibrary: () => set((state) => { state.isMediaLibraryOpen = !state.isMediaLibraryOpen; }),
    setZoom: (zoom) => set((state) => { state.zoomLevel = Math.max(0.25, Math.min(4, zoom)); }),

    // ── Save & undo ────────────────────────────────────────

    saveStory: async () => {
      const { story } = get();
      if (!story) return;
      set((state) => { state.isSaving = true; state.saveError = null; });
      try {
        const { metadata = {}, ...rest } = story;
        const payload = {
          ...rest,
          title: metadata.title ?? story.title ?? '',
          description: metadata.description ?? story.description ?? null,
          publisherName: metadata.publisherName ?? story.publisherName ?? '',
          storyMetadata: metadata,
        };
        const raw = await storiesApi.save(story.id, payload);
        const saved = normalizeStory(raw);
        set((state) => {
          const prevActivePageId = state.activePageId;
          state.story = saved;
          // Preserve active page selection if that page still exists after save
          const savedPageIds = new Set(saved.pages.map((p) => p.id));
          state.activePageId = savedPageIds.has(prevActivePageId)
            ? prevActivePageId
            : saved.pages[0]?.id ?? null;
          state.isSaving = false;
          state.isDirty = false;
          state.lastSavedAt = new Date().toISOString();
        });
      } catch (err) {
        set((state) => {
          state.isSaving = false;
          state.saveError = err instanceof Error ? err.message : 'Save failed';
        });
      }
    },

    autoSave: () => {
      const { isDirty, isSaving } = get();
      // if (isDirty && !isSaving) {
      //   get().saveStory();
      // }
    },

    pushHistory: (description) => set((state) => {
      if (!state.story) return;
      const entry = {
        pages: JSON.parse(JSON.stringify(state.story.pages)),
        description,
        timestamp: Date.now(),
      };
      // Truncate redo history
      state.history = state.history.slice(0, state.historyIndex + 1);
      state.history.push(entry);
      if (state.history.length > 50) state.history.shift(); // keep 50 max
      state.historyIndex = state.history.length - 1;
    }),

    undo: () => set((state) => {
      if (!state.story || state.historyIndex < 0) return;
      state.story.pages = JSON.parse(JSON.stringify(state.history[state.historyIndex].pages));
      state.historyIndex--;
      state.isDirty = true;
    }),

    redo: () => set((state) => {
      if (!state.story || state.historyIndex >= state.history.length - 1) return;
      state.historyIndex++;
      state.story.pages = JSON.parse(JSON.stringify(state.history[state.historyIndex].pages));
      state.isDirty = true;
    }),

    // ── Metadata ───────────────────────────────────────────

    updateStoryMetadata: (metadata) => set((state) => {
      if (!state.story) return;
      state.story.metadata = { ...state.story.metadata, ...metadata };
      state.isDirty = true;
    }),

    // ── Validation ─────────────────────────────────────────

    validateStory: async () => {
      const { story } = get();
      if (!story) return;
      try {
        const result = await storiesApi.validate(story.id);
        set((state) => { state.validationResult = result; });
      } catch {
        // ignore
      }
    },

    setValidationResult: (result) => set((state) => {
      state.validationResult = result;
    }),
  }))
);

// ── Helpers ──────────────────────────────────────────────────

function findPageForLayer(story, layerId) {
  if (!story) return null;
  return story.pages.find((p) => p.layers.some((l) => l.id === layerId)) ?? null;
}

function getNextZIndex(layers) {
  if (layers.length === 0) return 1;
  return Math.max(...layers.map((l) => l.transform.zIndex)) + 1;
}

function getDefaultLayerName(type) {
  const names = {
    text: 'Text',
    image: 'Image',
    video: 'Video',
    shape: 'Shape',
    cta: 'CTA Button',
    interactive: 'Interactive',
    form: 'Form',
    audio: 'Audio',
    overlay: 'Overlay',
    embed: 'Embed',
  };
  return names[type] ?? 'Layer';
}

// ── Utility hook for getting selected layer ──────────────────

export function useSelectedLayer() {
  return useEditorStore((state) => {
    if (state.selectedLayerIds.length !== 1) return null;
    const layerId = state.selectedLayerIds[0];
    const page = state.story?.pages.find((p) => p.layers.some((l) => l.id === layerId));
    return page?.layers.find((l) => l.id === layerId) ?? null;
  });
}

export function useActivePage() {
  return useEditorStore((state) => {
    if (!state.story || !state.activePageId) return null;
    return state.story.pages.find((p) => p.id === state.activePageId) ?? null;
  });
}
